import { Center, HStack, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Loading } from "semente-js";
import { toast } from "react-toastify";
import { AddButton } from "src/components/AddButton";
import CompanyIndicatorTypeService from "src/services/company/indicator_type";
import { IndicatorType } from "src/types";
import IndicatorTypeList from "./List";
import IndicatorTypeForm from "./Form";

const IndicatorTypeTab: React.FC = () => {
  const [indicatorTypeFormData, setIndicatorTypeFormData] = useState<
    IndicatorType | undefined
  >();
  const [indicatorTypes, setIndicatorTypes] = useState({
    data: [] as IndicatorType[],
    isLoading: true,
  });

  const fetchIndicatorTypes = async () => {
    try {
      const data = await CompanyIndicatorTypeService.get();
      const sortedData = data.sort((a, b) => a.order - b.order);
      setIndicatorTypes({ isLoading: false, data: sortedData });
    } catch (err) {
      toast.error("Ocorreu um erro ao buscar os tipos de indicadores");
      setIndicatorTypes({ data: [], isLoading: false });
    }
  };

  useEffect(() => {
    fetchIndicatorTypes();
  }, []);

  const handleCreateOrUpdateIndicatorType = async (values: IndicatorType) => {
    try {
      if ("id" in values && values.id) {
        const updatedIndicatorType = await CompanyIndicatorTypeService.update(
          values.id,
          values
        );
        const newIndicatorTypes = indicatorTypes.data.map((item) =>
          item.id === updatedIndicatorType.id ? updatedIndicatorType : item
        );
        setIndicatorTypes({
          ...indicatorTypes,
          data: newIndicatorTypes.sort((a, b) => a.order - b.order),
        });
      } else {
        const maxOrder = Math.max(
          ...indicatorTypes.data.map((i) => i.order),
          0
        );
        const newIndicatorType = await CompanyIndicatorTypeService.create({
          ...values,
          order: maxOrder + 1,
        });
        setIndicatorTypes({
          ...indicatorTypes,
          data: [...indicatorTypes.data, newIndicatorType].sort(
            (a, b) => a.order - b.order
          ),
        });
      }
      setIndicatorTypeFormData(undefined);
    } catch (err) {
      toast.error("Ocorreu um erro ao salvar o tipo de indicador");
    }
  };

  const handleReorder = async (id: string, direction: "up" | "down") => {
    const currentIndex = indicatorTypes.data.findIndex(
      (item) => item.id === id
    );
    if (currentIndex === -1) return;

    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= indicatorTypes.data.length) return;

    const currentItem = indicatorTypes.data[currentIndex];
    const targetItem = indicatorTypes.data[targetIndex];

    const newData = [...indicatorTypes.data];
    // Swap orders in the array items to reflect the swap
    // But we should swap the actual order property values if we want them to persist correctly with what server expects
    // Assuming backend respects the order we send
    const tempOrder = currentItem.order;
    const currentNewOrder = targetItem.order;
    const targetNewOrder = tempOrder;

    newData[currentIndex] = { ...currentItem, order: currentNewOrder };
    newData[targetIndex] = { ...targetItem, order: targetNewOrder };

    newData.sort((a, b) => a.order - b.order);
    setIndicatorTypes({ ...indicatorTypes, data: newData });

    try {
      await Promise.all([
        CompanyIndicatorTypeService.update(currentItem.id, {
          order: currentNewOrder,
        }),
        CompanyIndicatorTypeService.update(targetItem.id, {
          order: targetNewOrder,
        }),
      ]);
    } catch (err) {
      toast.error("Erro ao reordenar");
      fetchIndicatorTypes();
    }
  };

  return (
    <VStack flex={1} alignItems={"flex-start"} w={"100%"}>
      {indicatorTypeFormData !== undefined && (
        <IndicatorTypeForm
          handleSubmitIndicatorType={handleCreateOrUpdateIndicatorType}
          handleClose={() => setIndicatorTypeFormData(undefined)}
          indicatorType={indicatorTypeFormData}
        />
      )}

      <HStack justifyContent={"space-between"} width={"100%"} pb={"16px"}>
        <Text
          fontWeight={600}
          fontFamily={"Raleway"}
          fontSize={"RH_sm"}
          color={"Gray.$800"}
        >
          Tipos de Indicadores
        </Text>

        <AddButton
          label={`Adicionar novo tipo`}
          onClick={() => setIndicatorTypeFormData({} as IndicatorType)}
        />
      </HStack>

      {indicatorTypes.isLoading ? (
        <Center mt={"64px !important"} w={"100%"}>
          <Loading
            height="100"
            width="100"
            color="#408EC5"
            secondaryColor="#408EC5"
            radius="12.5"
            ariaLabel="mutating-dots-loading"
            visible={true}
          />
        </Center>
      ) : (
        <IndicatorTypeList
          indicatorTypes={indicatorTypes.data}
          handleEditIndicatorType={setIndicatorTypeFormData}
          handleReorder={handleReorder}
        />
      )}
    </VStack>
  );
};

export default IndicatorTypeTab;

